import { useFocusRing } from '@react-aria/focus'
import { useHover } from '@react-aria/interactions'
import { filterDOMProps, mergeProps } from '@react-aria/utils'
import cx from 'classnames'
import { forwardRef } from 'react'
import { useObjectRef } from 'utils/useObjectRef'
import { ButtonProps, ButtonSize } from './Types'
import Styles from './Button.module.scss'
import ChevronDown2 from 'blaze-ui/icons/20/ChevronDown2'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { DOMProps } from '@react-types/shared'
import { useStyleProps } from 'blaze-ui/utils/useStyleProps'
import useButtonWithScrollCancel from 'hooks/useButtonWithScrollCancel'

const iconSizes: Record<ButtonSize, number> = {
  xs: 16,
  sm: 20,
  md: 20,
  lg: 20,
  xl: 24,
}

export const iconSizesRedesign: Record<ButtonSize, number> = {
  xs: 16,
  sm: 16,
  md: 16,
  lg: 18,
  xl: 24,
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(({ className, ...props }, fRef) => {
  const ref = useObjectRef<HTMLButtonElement>(fRef)
  const { buttonProps, isPressed } = useButtonWithScrollCancel(props, ref)
  const { hoverProps, isHovered } = useHover(props)
  const { isFocusVisible, focusProps } = useFocusRing()
  const { styleProps } = useStyleProps(props)

  // Not using the useIsSystemRedesign hook because it would cause 100s of tests that are not wrapped in redux Providers and that render buttons to fail
  const isSystemRedesignEnabled = document.documentElement.getAttribute('data-autopilot-system-redesign') === 'on'

  const {
    children,
    size = 'md',
    variant = 'primary',
    iconClassName,
    useChildClass = false,
    frontIcon: FrontIcon,
    endIcon: EndIcon,
    isDisabled,
    title,
    withChevron,
    forceActive = false,
    ignoreHover = false,
    endIconClassName,
    fullWidth,
    rounded,
    square,
    vertical,
    justifyContent,
    ...finalProps
  } = props

  const iconSize = isSystemRedesignEnabled ? iconSizesRedesign[size] : iconSizes[size]

  return (
    <button
      title={title}
      {...mergeProps(
        filterDOMProps(finalProps),
        buttonProps,
        hoverProps,
        focusProps,
        (props.onPointerEnter
          ? { onPointerEnter: props.onPointerEnter, onPointerLeave: props.onPointerLeave }
          : {}) as DOMProps,
      )}
      className={cx(Styles.root, className, Styles[size], Styles[variant], {
        [Styles.hovered]: !ignoreHover && isHovered,
        [Styles.pressed]: isPressed || forceActive || props['aria-pressed'],
        [Styles.focused]: isFocusVisible,
        [Styles.disabled]: isDisabled,
        [Styles.fullWidth]: fullWidth,
        [Styles.rounded]: rounded,
        [Styles.square]: square,
        [Styles.vertical]: vertical,
        [Styles.iconButton]: !children && (!!FrontIcon || !!EndIcon),
      })}
      ref={ref}
      style={styleProps}
    >
      {FrontIcon && <FrontIcon size={iconSize} className={Styles.frontIcon} />}
      <ConditionalWrapper
        condition={!!children && !useChildClass}
        wrapper={(children) => <span className={Styles.children}>{children}</span>}
      >
        {children}
      </ConditionalWrapper>
      {EndIcon && <EndIcon size={iconSize} className={Styles.endIcon} />}
      {!!withChevron && (
        <span className={cx(Styles.chevron, Styles[withChevron])}>
          <ChevronDown2 aria-hidden="true" size={isSystemRedesignEnabled ? 15 : undefined} />
        </span>
      )}
    </button>
  )
})

Button.displayName = 'Button'

export { Button }
